import { AbstractMesh, Quaternion, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { ObstacleBehavior } from './ObstacleBehavior';

export class ObstacleRollBehavior extends ObstacleBehavior {
  private time: number = 0;
  private radius: number = 0.5;
  private lateralDirection: Vector3 = new Vector3(1, 0, 0);
  private rollAxis: Vector3 = new Vector3(0, 0, 1);

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleRoll', startPosition, pathIndex, config);
    this.pathIndex = pathIndex;
  }

  attach(mesh: AbstractMesh): void {
    super.attach(mesh);
    const bounds = mesh.getBoundingInfo().boundingBox;
    const height = bounds.maximumWorld.y - bounds.minimumWorld.y;
    this.radius = height / 2;
    if (this.radius <= 0.001) this.radius = 0.5;
  }

  initLevel(level: Level): ObstacleRollBehavior {
    super.initLevel(level);

    // Calculate lateral direction from path tangent at spawn position
    if (this.level && this.level.pathPoints && this.level.pathPoints.length > 1) {
      const idx = Math.floor(this.pathIndex);
      const prevIdx = Math.max(0, idx - 1);
      const nextIdx = Math.min(this.level.pathPoints.length - 1, idx + 1);

      const prev = this.level.pathPoints[prevIdx];
      const next = this.level.pathPoints[nextIdx];

      // Tangent along path (horizontal only to avoid tilting)
      const tangent = next.subtract(prev);
      tangent.y = 0; // Force horizontal to prevent wobbling on slopes
      tangent.normalize();

      // Lateral direction is perpendicular to tangent (in XZ plane)
      this.lateralDirection = new Vector3(-tangent.z, 0, tangent.x).normalize();

      // Roll axis = lateral direction (the axis of movement = the axis to roll around)
      this.rollAxis = this.lateralDirection.clone();
    }

    return this;
  }

  update(deltaTime: number): void {
    this.time += deltaTime * this.config.speed;
    const offset = Math.sin(this.time) * this.config.width;

    // Update position along lateral direction (perpendicular to path)
    this.mesh.position.x = this.startPosition.x + this.lateralDirection.x * offset;
    this.mesh.position.z = this.startPosition.z + this.lateralDirection.z * offset;

    // Roll angle: arc-length / radius
    const angle = offset / this.radius;

    // Rolling quaternion around the direction of movement (lateral axis)
    const rollQ = Quaternion.RotationAxis(Vector3.Forward(), angle);

    // Combine starting Y rotation (from alignToPath) with the rolling quaternion
    const baseQ = Quaternion.RotationAxis(Vector3.Up(), this.startRotation.y);
    this.mesh.rotationQuaternion = baseQ.multiply(rollQ);
  }

  clone(): ObstacleRollBehavior {
    return new ObstacleRollBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
